import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

const LAST_ADDED_STORAGE_KEY = 'theme-cart-drawer-last-added';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {boolean} */
  #showOnlyLastAdded = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = (event) => {
    const data = event.detail?.data;
    const sourceId = event.detail?.sourceId;
    const productId = data?.productId;
    const variantId = data?.variantId || sourceId;

    if (data?.didError) return;

    if (productId && variantId && data?.source !== 'quick-order-remove-all') {
      this.#storeLastAdded({ productId: productId.toString(), variantId: variantId.toString() });
      this.#showOnlyLastAdded = true;
      queueMicrotask(() => {
        this.#applyCartDisplayState();
        this.querySelector('cart-drawer-recommendations')?.loadForProduct(productId);
      });
    } else if (this.#showOnlyLastAdded) {
      queueMicrotask(() => this.#verifyLastAddedLine());
    }

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.#showOnlyLastAdded = false;
    this.#showAllCartLines();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  #storeLastAdded(product) {
    try {
      sessionStorage.setItem(LAST_ADDED_STORAGE_KEY, JSON.stringify(product));
    } catch {
      return;
    }
  }

  #readLastAdded() {
    try {
      const storedProduct = sessionStorage.getItem(LAST_ADDED_STORAGE_KEY);
      return storedProduct ? JSON.parse(storedProduct) : null;
    } catch {
      return null;
    }
  }

  /**
   * Identifies the line that was just added.
   *
   * Matching is variant-first on purpose: a shopper who already has size S in
   * the cart and then adds size XL should only see the XL line. Falling back to
   * the product id would match every size of that product.
   *
   * @param {HTMLElement} row
   * @param {{productId: string, variantId: string}} lastAdded
   * @returns {boolean}
   */
  #matchesLastAdded(row, lastAdded) {
    if (lastAdded.variantId) return row.dataset.variantId === lastAdded.variantId;
    return row.dataset.productId === lastAdded.productId;
  }

  #applyCartDisplayState() {
    if (!this.#showOnlyLastAdded) return;

    const lastAdded = this.#readLastAdded();
    const rows = this.refs.dialog?.querySelectorAll('.cart-items__table-row[data-key]');
    if (!lastAdded || !rows?.length) return;

    const matchingRows = [...rows].filter((row) => this.#matchesLastAdded(row, lastAdded));

    if (!matchingRows.length) return;

    const matchingKeys = new Set(matchingRows.map((row) => row.dataset.key));
    rows.forEach((row) => {
      row.hidden = !matchingRows.includes(row) && !matchingKeys.has(row.dataset.parentKey);
    });
  }

  #showAllCartLines() {
    this.refs.dialog?.querySelectorAll('.cart-items__table-row[hidden]').forEach((row) => {
      row.hidden = false;
    });
  }

  #verifyLastAddedLine() {
    const lastAdded = this.#readLastAdded();
    const rows = this.refs.dialog?.querySelectorAll('.cart-items__table-row[data-key]');
    if (!lastAdded || !rows?.length) return;

    const stillInCart = [...rows].some((row) => this.#matchesLastAdded(row, lastAdded));

    if (stillInCart) {
      this.#applyCartDisplayState();
      return;
    }

    try {
      sessionStorage.removeItem(LAST_ADDED_STORAGE_KEY);
    } catch {
      return;
    }

    this.#showOnlyLastAdded = false;
    this.#showAllCartLines();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
